import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import Cookies from 'js-cookie';
import { CircularProgress, Stack, Typography } from '@mui/joy';
import {
    APIGetMe, APILogin, APILogout,
    APIRegisterPushSubscription,
    registerUnauthorizedHandler,
    APIWorkstationActiveShift, APIWorkstationStartShift, APIWorkstationEndShift,
} from './api';
import { parseApiError } from './api';
import MemeLoader from './components/MemeLoader';

export const AppContext = createContext();

const TOKEN_KEY = 'photo_auth_token';
const VAPID_PUBLIC_KEY = 'BKEupBTkBs2m025EP8bTuLwQCuVI5FIGfmfFgNB4bQkgbn2mSF2BhnhjYh8fPV57xmDvpwfnYmq34aKAYTww0U0';

// ════════════ Push утилиты ════════════

const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
};

const getPlatform = () => {
    const ua      = navigator?.userAgent || '';
    const uaLower = ua.toLowerCase();
    const isIOS   = /iphone|ipod/.test(uaLower) ||
                    /ipad/.test(uaLower) ||
                    (navigator.maxTouchPoints > 1 && /macintosh/i.test(ua));
    if (isIOS)                     return 'ios';
    if (/android/.test(uaLower))   return 'android';
    if (/windows/.test(uaLower))   return 'windows';
    if (/mac/.test(uaLower))       return 'mac';
    return 'unknown';
};

const getIsStandalone = () =>
    !!(window?.navigator?.standalone ||
       window?.matchMedia('(display-mode: standalone)')?.matches);

const getOrCreateDeviceToken = () => {
    let t = localStorage.getItem('push_device_token');
    if (!t) {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        t = Array.from({ length: 16 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
        localStorage.setItem('push_device_token', t);
    }
    return t;
};

// ════════════════════════════════════════════════════════════════════
export default function AppProvider({ children }) {
    const navigate = useNavigate();

    // ── Auth ──────────────────────────────────────────────────────────
    const [token,       setToken]       = useState(() => Cookies.get(TOKEN_KEY) || null);
    const [account,     setAccount]     = useState(null);
    const [loadInitial, setLoadInitial] = useState(true);
    const [lock,        setLock]        = useState(false);

    // ── Push ──────────────────────────────────────────────────────────
    const [pushPermission,   setPushPermission]   = useState(() =>
        typeof Notification !== 'undefined' ? Notification.permission : 'default'
    );
    const [pushStatus,       setPushStatus]       = useState('idle');
    const [pushModalTest,    setPushModalTest]    = useState(false);
    const [pushModalIOSHelp, setPushModalIOSHelp] = useState(false);
    const [pwaStatus,        setPwaStatus]        = useState({
        platform:     getPlatform(),
        isStandalone: getIsStandalone(),
        needsInstall: false,
    });
    
    const [toast,     setToast]   = useState(null);
    const toastTimer  = useRef(null);

    // ════════════ Утилиты ════════════

    const showToast = useCallback((msg, color = 'success') => {
        if (toastTimer.current) clearTimeout(toastTimer.current);
        setToast({ msg, color });
        toastTimer.current = setTimeout(() => setToast(null), 3500);
    }, []);

    const saveToken = useCallback((t) => {
        setToken(t);
        if (t) Cookies.set(TOKEN_KEY, t, { expires: 30, sameSite: 'lax' });
        else   Cookies.remove(TOKEN_KEY);
    }, []);

    // Помощник: есть ли у пользователя хоть одно «менеджерское» право
    const hasManagerCapabilities = (acc) => {
        const perms = acc?.permissions || [];
        // Любое из этих прав = пользователь должен попадать в раздел руководителя
        const MANAGER_TAGS = [
            'schedule.view', 'schedule.edit',
            'schedule.change_requests.review', 'schedule.fill_requests.review',
            'schedule.substitutions.review', 'schedule.substitutions.view',
            'employees.view', 'employees.create', 'employees.update',
            'employees.fire', 'employees.restore', 'employees.delete',
            'employees.reset_password', 'employees.manage_admins',
            'finance.orders.create', 'finance.orders.view',
            'finance.fines.view', 'finance.fines.create',
            'finance.rewards.view', 'finance.rewards.create',
            'finance.sale_notifications.review', 'finance.stock.writeoff.review',
            'finance.orders.delete_requests.review', 'finance.configs.view',
            'finance.products.view', 'payment_numbers.view',
            'reports.view', 'reports.stats', 'reports.excel',
            'roles.view', 'events.view',
            'locations.create', 'locations.update', 'locations.delete', 'locations.change_default',
            'system.messages.view', 'system.messages.create',
        ];
        return MANAGER_TAGS.some(t => perms.includes(t));
    };

    // Куда вести ПО УМОЛЧАНИЮ (после входа и на "/"): на дашборд руководителя
    // или в личный раздел с графиком. Дашборд руководителя имеет смысл только
    // при «оверсайт»-правах (график, сотрудники, отчёты, заявки, роли, точки).
    // Точечные рабочие права админа (штрафы, премии, чеки своей точки и т.п.)
    // сюда НЕ входят — с ними человек остаётся на своём дашборде с графиком,
    // а функции доступны через меню. Так у админа с одними штрафами/премиями
    // не открывается пустой дашборд руководителя.
    const landsOnManagerDashboard = (acc) => {
        const perms = acc?.permissions || [];
        const DASHBOARD_TAGS = [
            'schedule.view', 'schedule.edit',
            'schedule.change_requests.review', 'schedule.fill_requests.review',
            'schedule.substitutions.review', 'schedule.substitutions.view',
            'employees.view', 'employees.create', 'employees.update',
            'employees.fire', 'employees.restore', 'employees.delete',
            'employees.reset_password', 'employees.manage_admins',
            'finance.orders.delete_requests.review',
            'finance.sale_notifications.review',
            'finance.stock.writeoff.review',
            'reports.view', 'reports.stats', 'reports.excel',
            'roles.view', 'events.view',
            'locations.create', 'locations.update', 'locations.delete', 'locations.change_default',
            'system.messages.view', 'system.messages.create',
        ];
        return DASHBOARD_TAGS.some(t => perms.includes(t));
    };

    // Редирект после авторизации: workstation → /workstation,
    // у кого есть «менеджерские» права → /manager, иначе → / (личный кабинет фотографа)
    const redirectByRole = useCallback((acc) => {
        if (acc?.role === 'workstation') {
            navigate('/workstation', { replace: true });
        } else if (landsOnManagerDashboard(acc)) {
            navigate('/manager', { replace: true });
        } else {
            navigate('/', { replace: true });
        }
    }, [navigate]);

    // ── Глобальный тик авто-обновления (каждые 30 мин) ────────────
    const refreshSubscribers = useRef(new Set());

    const onRefreshTick = useCallback((fn) => {
        refreshSubscribers.current.add(fn);
        return () => refreshSubscribers.current.delete(fn);
    }, []);

    // Принудительно обновить всё прямо сейчас (кнопка в хедере)
    const triggerRefresh = useCallback(async () => {
        const results = await Promise.allSettled(
            [...refreshSubscribers.current].map(fn => {
                try { return Promise.resolve(fn()); }
                catch (e) { return Promise.reject(e); }
            })
        );
        const allOk = results.every(r => r.status === 'fulfilled' && r.value !== false);
        if (allOk) {
            showToast('Данные обновлены ✓', 'success');
        } else {
            showToast('Ошибка при обновлении данных', 'danger');
        }
    }, [showToast]);

    useEffect(() => {
        const INTERVAL = 30 * 60 * 1000;
        let last = Date.now();

        const fire = () => {
            if (document.hidden) return;
            last = Date.now();
            triggerRefresh();
        };

        const id = setInterval(fire, INTERVAL);
        const onVisible = () => {
            if (!document.hidden && Date.now() - last >= INTERVAL) fire();
        };
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [triggerRefresh]);


     // ── Навигация по клику на push (sw.js шлёт {type:'navigate'}) ──
    useEffect(() => {
        const sw = navigator.serviceWorker;
        if (!sw) return;

        const onMessage = (event) => {
            if (event.data?.type !== 'navigate') return;
            let url = event.data.url || '/';
            const nt = event.data.notifType;

            // Уточняем маршрут по роли: фотограф не ходит в /manager/*
            const isSubst = [
                'substitution_available', 'substitution_request',
                'substitution_taken', 'substitution_approved', 'substitution_rejected',
            ].includes(nt);

            const isManagerSection = url.startsWith('/manager');
            if (isManagerSection && !hasManagerCapabilities(account)) {
                url = '/';
            }
            if (isSubst && !hasManagerCapabilities(account)) url = '/';

            navigate(url);
            window.focus();
        };

        sw.addEventListener('message', onMessage);
        return () => sw.removeEventListener('message', onMessage);
    }, [account, navigate]);

    // ── Обнаружение обновления Service Worker и автоперезагрузка ────────
    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;

        let reloading = false;

        // 1) SW сообщил, что активировалась новая версия → перезагружаем
        const onMessage = (event) => {
            if (event.data?.type === 'sw_updated' && !reloading) {
                reloading = true;
                showToast('Обновление приложения... 🔄');
                setTimeout(() => window.location.reload(), 800);
            }
        };
        navigator.serviceWorker.addEventListener('message', onMessage);

        // 2) При смене controller'а (новая SW взяла управление) — тоже перезагрузка
        const onControllerChange = () => {
            if (!reloading) {
                reloading = true;
                window.location.reload();
            }
        };
        navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

        // 3) Периодически проверяем апдейты SW (когда юзер возвращается на вкладку)
        const checkForUpdate = async () => {
            try {
                const reg = await navigator.serviceWorker.getRegistration();
                if (reg) reg.update();
            } catch (e) { /* ignore */ }
        };
        const onVisible = () => { if (!document.hidden) checkForUpdate(); };
        document.addEventListener('visibilitychange', onVisible);

        // первый check при загрузке (через 5 сек чтобы не ддосить старт)
        const initialTimer = setTimeout(checkForUpdate, 5000);
        // и потом каждые 5 минут
        const interval = setInterval(checkForUpdate, 5 * 60 * 1000);

        return () => {
            navigator.serviceWorker.removeEventListener('message', onMessage);
            navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
            document.removeEventListener('visibilitychange', onVisible);
            clearTimeout(initialTimer);
            clearInterval(interval);
        };
    }, [showToast]);
        

    // ── Workstation: активные смены (теперь несколько админов одновременно) ───
    const [activeShifts, setActiveShifts] = useState([]);

    // Для обратной совместимости: «последняя начатая смена»
    const activeShift = activeShifts.length > 0 ? activeShifts[activeShifts.length - 1] : null;

    const isWorkstation = account?.role === 'workstation';

    const loadActiveShift = useCallback(async () => {
        if (!isWorkstation || !token) { setActiveShifts([]); return; }
        const data = await APIWorkstationActiveShift(token);
        if (data?.active && Array.isArray(data.shifts)) {
            setActiveShifts(data.shifts);
        } else if (data?.active && data.shift) {
            // fallback на старый формат
            setActiveShifts([data.shift]);
        } else {
            setActiveShifts([]);
        }
    }, [isWorkstation, token]);

    useEffect(() => {
        if (isWorkstation && token) loadActiveShift();
    }, [isWorkstation, token, loadActiveShift]);

    // Раз в 5 минут перезапрашиваем активную смену — если бэк её авто-закрыл
    // после 05:00 МСК (например, воркстанция простояла открытой всю ночь),
    // фронт сам подхватит «нет активной смены» без перезагрузки страницы.
    useEffect(() => {
        if (!isWorkstation || !token) return;
        const id = setInterval(() => {
            if (!document.hidden) loadActiveShift();
        }, 5 * 60 * 1000);
        return () => clearInterval(id);
    }, [isWorkstation, token, loadActiveShift]);

    // Начать новую смену (для конкретного админа + опц. список фотографов)
    const startShift = useCallback(async (adminId, photographerIds = []) => {
        const res = await APIWorkstationStartShift(token, adminId, photographerIds);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось начать смену'), 'danger');
            return { ok: false, error: res.error };
        }
        // Перезагружаем общий список смен (на этой workstation теперь могут быть и другие)
        await loadActiveShift();
        showToast('Смена начата', 'success');
        return { ok: true, shift: res };
    }, [token, showToast, loadActiveShift]);

    // Завершить конкретную смену (опционально - если одна, можно без shiftId)
    const endShift = useCallback(async (shiftId = null) => {
        // Если shiftId не передан и активных смен больше одной — отказ
        if (!shiftId && activeShifts.length > 1) {
            showToast('Несколько активных смен — выберите какую завершить', 'warning');
            return { ok: false };
        }
        const targetId = shiftId || (activeShifts[0]?.id ?? null);
        const res = await APIWorkstationEndShift(token, targetId);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось завершить смену'), 'danger');
            return { ok: false };
        }
        await loadActiveShift();
        showToast('Смена завершена', 'success');
        return { ok: true };
    }, [token, showToast, activeShifts, loadActiveShift]);

    // ════════════ АВТО-ЛОГАУТ ПРИ 401 ════════════

    const forceLogout = useCallback(() => {
        saveToken(null);
        setAccount(null);
        setLock(true);
        showToast('Сессия истекла. Войдите заново.', 'warning');
        navigate('/login', { replace: true });
    }, [saveToken, navigate, showToast]);

    useEffect(() => {
        registerUnauthorizedHandler(forceLogout);
    }, [forceLogout]);

    // ════════════ АВТОРИЗАЦИЯ ════════════

    const verifyToken = useCallback(async () => {
        const saved = Cookies.get(TOKEN_KEY);
        if (!saved) { setLock(true); setLoadInitial(false); return; }
        const data = await APIGetMe(saved);
        if (data?.id) {
            setToken(saved); setAccount(data); setLock(false);
        } else {
            Cookies.remove(TOKEN_KEY); setToken(null); setLock(true);
        }
        setLoadInitial(false);
    }, []);

    // ← ВЕРНУЛИ: запускаем verifyToken при монтировании
    useEffect(() => { verifyToken(); }, [verifyToken]);

    // Гарантируем, что workstation всегда находится в своей зоне маршрутов
    useEffect(() => {
        if (loadInitial || lock || !account) return;
        if (account.role === 'workstation' && !window.location.pathname.startsWith('/workstation')) {
            navigate('/workstation', { replace: true });
        }
    }, [account, lock, loadInitial, navigate]);

    const login = useCallback(async (email, password) => {
        const data = await APILogin(email, password);
        if (data?.token) {
            saveToken(data.token);
            setAccount(data.account);
            setLock(false);
            redirectByRole(data.account);   // ← роль-зависимый редирект
            return { ok: true };
        }
        return { ok: false, error: data?.error || 'Неверный email или пароль' };
    }, [saveToken, redirectByRole]);

    const logout = useCallback(async () => {
        if (token) await APILogout(token);
        saveToken(null); setAccount(null);
        setLock(true); navigate('/login', { replace: true });
    }, [token, saveToken, navigate]);

    // ════════════ PUSH ════════════

    const isPushSupported = () =>
        'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

    const sendSubscriptionToServer = useCallback(async (subscription) => {
        if (!token) return { type: 'no_token' };
        try {
            const p256dh = btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh'))));
            const auth   = btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))));
            await APIRegisterPushSubscription(token, {
                endpoint:     subscription.endpoint,
                p256dh_key:   p256dh,
                auth_key:     auth,
                browser:      navigator.userAgent.slice(0, 200),
                platform:     getPlatform(),
                device_token: getOrCreateDeviceToken(),
            });
            return { type: 'success', subscription };
        } catch (err) {
            console.error('sendSubscriptionToServer:', err);
            return { type: 'server_error', err };
        }
    }, [token]);

    const processPushSubscription = useCallback(async () => {
        if (!isPushSupported()) return { type: 'not_supported' };
        try {
            let reg;
            try {
                reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
                await navigator.serviceWorker.ready;
            } catch (e) {
                console.error('SW error:', e);
                return { type: 'service_worker_error' };
            }

            if (getPlatform() === 'ios' && !getIsStandalone()) {
                setPushModalIOSHelp(true);
                return { type: 'ios_need_install' };
            }

            let sub = await reg.pushManager.getSubscription();
            if (!sub) {
                if (!VAPID_PUBLIC_KEY) { console.error('VAPID_PUBLIC_KEY не задан'); return { type: 'no_vapid_key' }; }
                sub = await reg.pushManager.subscribe({
                    userVisibleOnly:      true,
                    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
                });
            }
            return await sendSubscriptionToServer(sub);
        } catch (err) {
            console.error('processPushSubscription:', err);
            return { type: 'error', error: err.message };
        }
    }, [sendSubscriptionToServer]);

    const requestPush = useCallback(async () => {
        if (!isPushSupported()) {
            showToast('Push-уведомления не поддерживаются вашим браузером', 'warning');
            return { type: 'not_supported' };
        }

        const platform     = getPlatform();
        const isStandalone = getIsStandalone();

        if (platform === 'ios' && !isStandalone) {
            setPushModalIOSHelp(true);
            return { type: 'ios_need_install' };
        }

        const current = Notification.permission;

        if (current === 'granted') {
            setPushStatus('requesting');
            const result = await processPushSubscription();
            setPushStatus(result.type === 'success' ? 'success' : 'error');
            if (result.type === 'success') { showToast('Push-уведомления активны 🔔'); setPushModalTest(true); }
            return result;
        }

        if (current === 'denied') {
            if (platform === 'ios') {
                setPushModalIOSHelp(true);
            } else if (platform === 'android') {
                showToast('Push заблокированы. Нажмите 🔒 → «Разрешения» → «Уведомления» → Разрешить', 'warning');
            } else {
                showToast('Push заблокированы. Нажмите 🔒 в адресной строке → «Настройки сайта» → Уведомления → Разрешить', 'warning');
            }
            return { type: 'permission_denied_permanent' };
        }

        setPushStatus('requesting');
        let granted;
        try { granted = await Notification.requestPermission(); }
        catch (e) { setPushStatus('error'); return { type: 'error' }; }

        setPushPermission(granted);

        if (granted === 'granted') {
            const result = await processPushSubscription();
            setPushStatus(result.type === 'success' ? 'success' : 'error');
            if (result.type === 'success') { showToast('Push-уведомления подключены 🔔'); setPushModalTest(true); }
            else                           { showToast('Ошибка подключения Push', 'danger'); }
            return result;
        }

        setPushStatus('idle');
        showToast('Разрешение на уведомления не выдано', 'warning');
        return { type: 'permission_denied' };
    }, [processPushSubscription, showToast]);

    // PWA статус
    useEffect(() => {
        const isStandalone = getIsStandalone();
        const platform     = getPlatform();
        const isMobile     = platform === 'ios' || platform === 'android';
        setPwaStatus({ platform, isStandalone, needsInstall: isMobile && !isStandalone });

        const mq = window?.matchMedia('(display-mode: standalone)');
        const handler = () => setPwaStatus(prev => ({
            ...prev, isStandalone: getIsStandalone(),
            needsInstall: (prev.platform === 'ios' || prev.platform === 'android') && !getIsStandalone(),
        }));
        mq?.addEventListener('change', handler);
        return () => mq?.removeEventListener('change', handler);
    }, []);

    // Авто-подписка на Push
    useEffect(() => {
        if (!token || lock || loadInitial || pwaStatus.needsInstall) return;
        if (typeof Notification === 'undefined') return;

        if (Notification.permission === 'granted') {
            processPushSubscription();
        } else if (Notification.permission === 'default') {
            const timer = setTimeout(() => { requestPush(); }, 1500);
            return () => clearTimeout(timer);
        }
    }, [token, lock, loadInitial, pwaStatus.needsInstall]); // eslint-disable-line

    // ════════════ VALUE ════════════

    // Хелпер для проверки прав на фронте
    const hasPerm = useCallback((...tags) => {
        const perms = account?.permissions || [];
        return tags.every(t => perms.includes(t));
    }, [account]);

    const hasAnyPerm = useCallback((...tags) => {
        const perms = account?.permissions || [];
        return tags.some(t => perms.includes(t));
    }, [account]);

    const value = {
        token, account, lock, setLock, login, logout, loadInitial,
        hasPerm, hasAnyPerm, setAccount,
        hasManagerCapabilities: () => hasManagerCapabilities(account),
        landsOnManagerDashboard: () => landsOnManagerDashboard(account),
        isWorkstation, activeShift, activeShifts, loadActiveShift, startShift, endShift,
        pushPermission, pushStatus, pushModalTest, setPushModalTest,
        pushModalIOSHelp, setPushModalIOSHelp, pwaStatus, requestPush,
        toast, showToast, onRefreshTick, triggerRefresh,
    };

    if (loadInitial) return <VerificationScreen />;

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

function VerificationScreen() {
    return (
        <Stack sx={{
            position: 'fixed', inset: 0, zIndex: 99999,
            bgcolor: '#08080f', justifyContent: 'center', alignItems: 'center', gap: 2,
        }}>
            <MemeLoader />
        </Stack>
    );
}